import React, { useState } from 'react';
import { FileCheck, Download } from 'lucide-react';
import { useVendorTaxReports } from '../../hooks/useVendorTaxReports';
import CustomSelect from '../../components/shared/CustomSelect';
import api from '../../services/authService';

const VendorTaxReports = () => {
  const [year, setYear] = useState(new Date().getFullYear());

  const {
    reports,
    isLoading,
    error,
    formatAmount,
    generateYearOptions
  } = useVendorTaxReports(year);
  
  return (
    <>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Tax Reports</h1>
            <p className="text-sm text-gray-600">Generate and download tax reports for your records</p>
          </div>
                  <div className="flex items-center gap-3 mt-4 sm:mt-0">
                    <div className="w-full sm:w-48">
                      <CustomSelect
                        value={year.toString()}
                        onChange={(value) => setYear(parseInt(value))}
                        options={generateYearOptions().map((y) => ({
                          value: y.toString(),
                          label: y.toString()
                        }))}
                        size="sm"
                        className="w-full"
                      />
                    </div>
            <button
              onClick={async () => {
                try {
                  const response = await api.get(`/vendor/tax-reports/download?year=${year}&format=csv`, {
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `tax-report-${year}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error('Failed to download report:', error);
                  alert('Failed to download report. Please try again.');
                }
              }}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm w-full sm:w-auto justify-center"
            >
              <Download className="w-4 h-4" />
              <span>Download Report</span>
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            Failed to load tax reports. Please try again.
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tax Summary for {year}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-xl font-bold text-gray-900">
                    ${formatAmount(reports.total_revenue)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Expenses</p>
                  <p className="text-xl font-bold text-gray-900">
                    ${formatAmount(reports.total_expenses)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Net Income</p>
                  <p className="text-xl font-bold text-green-600">
                    ${formatAmount(reports.net_income)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Tax Owed</p>
                  <p className="text-xl font-bold text-red-600">
                    ${formatAmount(reports.tax_owed)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Reports</h2>
              {reports.quarterly_reports && reports.quarterly_reports.length > 0 ? (
                <div className="space-y-3">
                  {reports.quarterly_reports.map((report, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 border-b border-gray-100 last:border-0 gap-3">
                      <div className="flex items-center gap-3">
                        <FileCheck className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{report.quarter}</p>
                          <p className="text-sm text-gray-500">
                            Revenue: ${formatAmount(report.revenue)}
                          </p>
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          const quarter = report.quarter.replace(` ${year}`, '').toLowerCase();
                          const url = `/vendor/tax-reports/download?year=${year}&quarter=${quarter}&format=csv`;
                          window.open(url, '_blank');
                        }}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm w-full sm:w-auto justify-center"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No tax reports available for {year}</p>
                  <p className="text-sm text-gray-500 mt-2">Generate a report to get started</p>
                </div>
              )}
            </div>
          </>
        )}
    </>
  );
};

export default VendorTaxReports;

